//
import moment from 'moment';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
//
import { useAppConfig } from '@state';
import { useDebounce, useSearchParams } from '@hooks';
import { hotkeys } from '@ohif/core';

import i18n from '@ohif/i18n';
import { useModal, UserPreferences } from '@ohif/ui';
import { Table } from '@vindr/ui';
import useDragResizeColumn from '../../hooks/useDragResizeColumn';
import useDefinedColumns from './useDefinedColumns';
import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import {
  INITIAL_SORTER_STUDYLIST,
  STUDYLIST_COLUMNS_ORIGINAL,
  STUDYLIST_COLUMNS_SETTING,
} from '@vindr/services/constants/study.constant';
import isEmpty from 'lodash/isEmpty';
import Filter from './Filter';
import PopupContextMenu from '@vindr/ui/src/components/ui/PopupContextMenu';
import useContextMenu, { MENU_TYPE } from './useContextMenu';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';

const { availableLanguages, defaultLanguage, currentLanguage } = i18n;

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */
function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
}) {
  const [pagination, setPagination] = useState(INIT_PAGINATION);
  const [sorter, setSorter] = useState(INITIAL_SORTER_STUDYLIST);
  const { hotkeyDefinitions, hotkeyDefaults } = hotkeysManager;
  const { show, hide } = useModal();
  const { t } = useTranslation();
  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Filters
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const queryFilterValues = _getQueryFilterValues(searchParams);
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...queryFilterValues,
  });

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 101 studies
   */

  const sortedStudies = useMemo(() => {
    const { sort_field: sortBy, sort_order } = sorter;
    const sortModifier = sort_order === SORT_ORDER.DESC ? -1 : 1;
    return studies.sort((s1, s2) => {
      const s1Prop = s1[sortBy];
      const s2Prop = s2[sortBy];

      if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
        return s1Prop.localeCompare(s2Prop) * sortModifier;
      } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
        return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
      } else if (!s1Prop && s2Prop) {
        return -1 * sortModifier;
      } else if (!s2Prop && s1Prop) {
        return 1 * sortModifier;
      } else if (sortBy === 'study_date') {
        return _sortStringDates(s1, s2, sortModifier);
      }

      return 0;
    });
  }, [sorter, studies]);

  const numOfStudies = studiesTotal;

  const setFilterValues = (val) => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues({
      ...val,
      studyDate: {
        startDate: moment(val.study_date_from).format('YYYYMMDD'),
        endDate: moment(val.study_date_to).format('YYYYMMDD'),
      },
      patientName: val.patient_name,
      modalities: [val.modality],
      mrn: val.patient_id,
      accession: val.order_group,
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  // Sync URL query parameters with filters
  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    const queryString = {};
    Object.keys(defaultFilterValues).forEach((key) => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];

      // TODO: nesting/recursion?
      if (key === 'studyDate') {
        if (
          currValue.startDate &&
          defaultValue.startDate !== currValue.startDate
        ) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'modalities' && currValue.length) {
        queryString.modalities = currValue.join(',');
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });

    navigate({
      pathname: '/',
      search: search ? `?${search}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues]);

  const offset = pagination.size * (pagination.page - 1);
  const offsetAndTake = offset + pagination.size;

  const menuOptions = [
    {
      title: t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          title: t('UserPreferencesModal:User Preferences'),
          content: UserPreferences,
          contentProps: {
            hotkeyDefaults:
              hotkeysManager.getValidHotkeyDefinitions(hotkeyDefaults),
            hotkeyDefinitions,
            onCancel: hide,
            currentLanguage: currentLanguage(),
            availableLanguages,
            defaultLanguage,
            onSubmit: (state) => {
              i18n.changeLanguage(state.language.value);
              hotkeysManager.setHotkeys(state.hotkeyDefinitions);
              hide();
            },
            onReset: () => hotkeysManager.restoreDefaultBindings(),
            hotkeysModule: hotkeys,
          },
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(
          `/logout?redirect_uri=${encodeURIComponent(window.location.href)}`
        );
      },
    });
  }
  const studylist = sortedStudies.slice(offset, offsetAndTake);

  const performedColumns = useDefinedColumns(INIT_PAGINATION);

  const [contextMenuState, setContextMenuState] = useState({
    currentRecord: null,
    selectedRecords: [],
    visible: false,
    x: 0,
    y: 0,
  });

  const { components, resizableColumns, tableWidth, dragProps } =
    useDragResizeColumn(
      performedColumns,
      STUDYLIST_COLUMNS_SETTING,
      STUDYLIST_COLUMNS_ORIGINAL,
      setContextMenuState
    );

  const openViewer = (studyUID, modality) => {
    const isModalitySM =
      modality && (modality === 'SM' || modality.includes('SM'));
    const urlViewer = `/${
      isModalitySM ? 'microscopy' : 'default'
    }?StudyInstanceUIDs=${studyUID}`;
    console.log('🚀 ~ openViewer ~ urlViewer:', urlViewer);

    navigate(urlViewer);
  };

  const handleContextMenuSelect = useCallback(
    (type: keyof typeof MENU_TYPE, record) => {
      switch (type) {
        case MENU_TYPE.OPEN_SPLIT_WINDOW:
          setCollapsedLeft(true);
          setOpenIframe(record.study_instance_uid, record?.modality);
          break;
        case MENU_TYPE.OPEN_SPLIT_WINDOW_AND_REPORT:
          setCollapsedLeft(true);
          setOpenIframe(record.study_instance_uid, record?.modality);
          openReport(record?.study_id);
          break;
        case MENU_TYPE.OPEN_VIEWER:
          openViewer(record.studyInstanceUid, record?.modalities);
          break;
        case MENU_TYPE.OPEN_VIEWER_AND_REPORT:
          openViewer(record.study_instance_uid, record?.modality);
          openReport(record?.study_id);
          break;
        case MENU_TYPE.ORDER_MATCH:
          setMatchOrderState({ visible: true, study: record });
          break;
        case MENU_TYPE.OPEN_REPORT:
          openReport(record?.study_id);
          break;
        case MENU_TYPE.SHARE_STUDY:
          setShareState({ visible: true, study_id: record?.study_id });
          break;
        case MENU_TYPE.ATTACHMENT_FILE:
          setAttachmentState({
            visible: true,
            study: record,
            study_id: record?.study_id,
          });
          break;
        default:
          break;
      }
      setContextMenuState({ ...contextMenuState, visible: false });
    },
    []
  );

  const contextMenuItems = useContextMenu({ handleContextMenuSelect });

  const contextMenu = useMemo(() => {
    return {
      items: contextMenuItems,
      onClick: (menuItem, record) => {
        if (menuItem.onClick) {
          menuItem.onClick(record);
        }
      },
    };
  }, [contextMenuItems]);

  const contextMenuProps = contextMenu
    ? {
        ...contextMenu,
        onClick: (it) =>
          contextMenu.onClick(it, contextMenuState.currentRecord),
      }
    : false;

  const onRow = useCallback(
    (record: typeof studylist[number]) => {
      return {
        // onClick: (event: MouseEvent) => onClickRow({ event, setSelectedRowKeys, record }),
        onDoubleClick: (event: MouseEvent) => {
          event.stopPropagation();
          event.preventDefault();
          const target = event?.target as HTMLElement;

          if (
            target?.nodeName === 'INPUT' ||
            target.classList.contains('ant-table-cell-fix-right')
          ) {
            // handle action doubleClick to checkbox
            return;
          }
          contextMenuItems[0].onClick(record);
        },
      };
    },
    [contextMenuItems]
  );

  const handlePaginationChange = ({ page, size }) => {
    console.log(page, size);
    const oldPageNumber = filterValues.pageNumber;
    const rollingPageNumberMod = Math.floor(101 / filterValues.resultsPerPage);
    const rollingPageNumber = oldPageNumber % rollingPageNumberMod;
    const isNextPage = page > oldPageNumber;
    const hasNextPage =
      Math.max(rollingPageNumber, 1) * resultsPerPage < numOfStudies;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setPagination({ page, size });
  };

  const handleForceSearch = () => {
    setFilterValues({
      ...filterValues,
    });
  };

  const onTableChange = useCallback(
    (_, __, sorter) => {
      setSorter({
        sort_field: sorter.field,
        sort_order: sorter.order === 'ascend' ? 0 : 1,
      });
    },
    [setSorter]
  );

  return (
    <div className="flex h-screen flex-col bg-background-dark-3">
      {/* <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      /> */}
      <div className="flex h-full w-full flex-col overflow-auto rounded-sm bg-background-light-3 p-4 dark:bg-background-dark-3">
        <Filter forceSearch={handleForceSearch} onChange={setFilterValues} />
        <Table
          columns={resizableColumns}
          components={components}
          // contextMenu={contextMenu}
          count={numOfStudies}
          dataSource={studylist}
          loading={isLoadingData || isEmpty(resizableColumns)}
          pagination={pagination}
          scroll={{ x: tableWidth }}
          // setSelectedRowKeys={setSelectedRowKeys}
          wrapperClassName="min-h-[210px]"
          // rowKey={(record) =>
          //   generateRowKey([record.study_instance_uid, record.study_id, record.patient_id])
          // }
          // rowSelection={{
          //   type: 'checkbox',
          //   selectedRowKeys: selectedRowKeys.map((x) =>
          //     generateRowKey([x.study_instance_uid, x.study_id, x.patient_id])
          //   ),
          //   onChange: (keys, records) => {
          //     setSelectedRowKeys(records);
          //   },
          // }}
          // selectedRowKeys={selectedRowKeys.map((x) =>
          //   generateRowKey([x.study_instance_uid, x.study_id, x.patient_id])
          // )}
          onPaginationChange={handlePaginationChange}
          onRow={onRow}
          onTableChange={onTableChange}
        />
        {contextMenu && contextMenuProps && contextMenuProps?.items?.length ? (
          <PopupContextMenu
            menu={contextMenuProps}
            {...contextMenuState}
            {...{ setContextMenuState }}
          />
        ) : null}
      </div>
    </div>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
};

const defaultFilterValues = {
  patientName: '',
  mrn: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  description: '',
  modalities: [],
  accession: '',
  sortBy: '',
  sortDirection: 'none',
  pageNumber: 1,
  resultsPerPage: 25,
  datasources: '',
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const queryFilterValues = {
    patientName: params.get('patientname'),
    mrn: params.get('mrn'),
    studyDate: {
      startDate: params.get('startdate') || null,
      endDate: params.get('enddate') || null,
    },
    description: params.get('description'),
    modalities: params.get('modalities')
      ? params.get('modalities').split(',')
      : [],
    accession: params.get('accession'),
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    (key) => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // TODO: Delimiters are non-standard. Should we support them?
  const s1Date = moment(s1.date + ' ' + s1.time);
  const s2Date = moment(s2.date + ' ' + s2.time);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (
      (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier
    );
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default memo(WorkList);
